import { useEffect, useState } from 'react';

import { StatePanel } from './StatePanel';
import styles from './PendingPanel.module.css';

/**
 * Long enough that the first email has realistically had time to arrive, so the
 * button is a genuine second attempt rather than an impatient double-send that
 * only harms sender reputation.
 */
const COOLDOWN_SECONDS = 60;

interface PendingPanelProps {
  email: string;
  /** Resolves true if the provider accepted the repeat submission. */
  onResend: () => Promise<boolean>;
}

function formatCountdown(seconds: number): string {
  return `0:${String(seconds).padStart(2, '0')}`;
}

/**
 * Shown once a sign-up is accepted and the confirmation email is pending.
 *
 * The copy has to hold for two situations we deliberately cannot tell apart:
 * a genuinely new sign-up, and someone already on the list. The provider
 * answers the same either way, and reporting the difference would let anyone
 * test which addresses are subscribed. So it never claims an email was
 * definitely just sent to this person.
 */
export function PendingPanel({ email, onResend }: PendingPanelProps) {
  const [secondsLeft, setSecondsLeft] = useState(COOLDOWN_SECONDS);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);

  // One timeout per tick, cleaned up on each - no interval to leak if the
  // component unmounts mid-countdown.
  useEffect(() => {
    if (secondsLeft === 0) return;
    const id = window.setTimeout(() => {
      setSecondsLeft((current) => current - 1);
    }, 1000);
    return () => {
      window.clearTimeout(id);
    };
  }, [secondsLeft]);

  async function handleResend(): Promise<void> {
    setBusy(true);
    const accepted = await onResend();
    setBusy(false);
    setResent(accepted);
    setSecondsLeft(COOLDOWN_SECONDS);
  }

  const waiting = secondsLeft > 0;

  return (
    <StatePanel
      icon="✉"
      title="Check your inbox"
      live
      extra={
        <>
          <div className={styles.notice}>
            <svg className={styles.noticeIcon} viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7.5v5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16.4" r="1.15" fill="currentColor" />
            </svg>
            <p className={styles.noticeText}>
              <strong>Check spam and promotions first.</strong> We're a new sender, so our mail often lands
              there before it earns its way to the inbox. Marking it "not spam" helps.
            </p>
          </div>

          <div className={styles.resendRow}>
            <button
              type="button"
              className={styles.resendBtn}
              onClick={() => void handleResend()}
              disabled={waiting || busy}
            >
              {busy ? 'Sending…' : waiting ? `Resend in ${formatCountdown(secondsLeft)}` : 'Resend email'}
            </button>
            <p className={styles.resendNote} role="status" aria-live="polite">
              {resent
                ? 'Sent again. Give it a minute, and check spam once more.'
                : 'Already confirmed on a previous visit? You’re on the list — nothing else to do.'}
            </p>
          </div>
        </>
      }
    >
      We've sent a confirmation link to <strong>{email}</strong>. Click it to lock in your place on the
      waitlist.
    </StatePanel>
  );
}
